import {
  Building,
  Calendar,
  CalendarDaysIcon,
  CardIdentity,
  ChartColumnIcon,
  ChevronLeft,
  CogIcon,
  GroupPeople,
  ClipboardListIcon,
  GamepadIcon,
  GraphIcon,
  HandshakeIcon,
  InboxIcon,
  IntercomIcon,
  LayoutDashboardIcon,
  Lightning,
  MissionIcon,
  PersonIcon,
  PhoneIcon,
  ReceiptEuroIcon,
  ReceiptIcon,
  StarOutline,
  TrendingUpIcon,
  UserSearchIcon,
  WalletIcon,
} from 'assets/icons';
import {
  canWorkWithCandidates,
  canProspect,
  canSeeStats,
  canWorkWithCompanies,
  RECRUITER_ROLE,
  STAFF_ROLES,
} from 'helper/roles';
import { dayTitle } from 'helper/timeLabels';

const isStaff = (user) => STAFF_ROLES.includes(user?.role);
const worksWithCompanies = (user) => canWorkWithCompanies(user?.role);
const worksWithCandidates = (user) => canWorkWithCandidates(user?.role);
const isRecruiter = (user) => user?.role === RECRUITER_ROLE;
const mayProspect = (user) => canProspect(user?.role);
const maySeeStats = (user) => canSeeStats(user?.role);

/**
 * Every page of the menu. `match` lists the other paths that belong to a page
 * (its detail pages), `show` hides it from whoever cannot open it, and `label`
 * is the French fallback of its `sidebar.<key>` locale entry. The navbar titles
 * the page with `label`, or `pageTitle()` when the link has one.
 */
export const LINKS = {
  dashboard: {
    key: 'dashboard',
    label: 'Accueil',
    to: '/dashboard',
    exact: true,
    icon: LayoutDashboardIcon,
    // The home page is titled with today's date.
    pageTitle: () => dayTitle(new Date()),
  },
  tasks: {
    key: 'tasks',
    label: 'Mes tâches',
    to: '/tasks',
    icon: ClipboardListIcon,
    show: worksWithCompanies,
  },
  prospection: {
    key: 'prospection',
    label: 'Prospection',
    to: '/prospection',
    icon: PhoneIcon,
    show: mayProspect,
  },
  stats: {
    key: 'stats',
    label: 'Statistiques',
    to: '/statistics',
    icon: ChartColumnIcon,
    show: maySeeStats,
  },
  companies: {
    key: 'companies',
    label: 'Entreprises',
    to: '/company',
    icon: Building,
    show: worksWithCompanies,
  },
  planning: {
    key: 'planning',
    label: 'Planning',
    to: '/planning',
    icon: CalendarDaysIcon,
    show: worksWithCompanies,
  },
  missions: {
    key: 'missions',
    label: 'Missions',
    to: '/missions',
    icon: MissionIcon,
  },
  candidates: {
    key: 'candidates',
    label: 'Candidats',
    to: '/vivier',
    exact: true,
    icon: PersonIcon,
    show: worksWithCandidates,
  },
  pools: {
    key: 'pools',
    label: 'Viviers',
    to: '/listAllVivier',
    match: ['/ficheVivier'],
    icon: InboxIcon,
    show: worksWithCandidates,
  },
  reviews: {
    key: 'reviews',
    label: 'Avis',
    to: '/vivier-rating-temp-worker/rating-untreated',
    match: ['/vivier-rating-temp-worker'],
    icon: StarOutline,
    show: isStaff,
  },
  identityValidation: {
    key: 'identity_validation',
    label: 'Validation d’identité',
    to: '/identity_validation',
    icon: CardIdentity,
    show: worksWithCandidates,
  },
  qualifications: {
    key: 'qualifications',
    label: 'Qualifications',
    to: '/qualification',
    icon: Lightning,
    show: isStaff,
  },
  timesheets: {
    key: 'timesheets',
    label: 'Relevés d’heures',
    to: '/timeSheet/toBeCompleted',
    match: ['/timeSheet'],
    icon: Calendar,
    show: isStaff,
  },
  payroll: {
    key: 'payroll',
    label: 'Paie',
    to: '/payroll/payslips',
    match: ['/payroll'],
    icon: ReceiptEuroIcon,
    show: (user) => isStaff(user) && !!user.accessPayslip,
  },
  billing: {
    key: 'billing',
    label: 'Facturation',
    to: '/billing/invoices',
    match: ['/billing'],
    icon: WalletIcon,
    show: (user) => isStaff(user) && !!user.accessInvoice,
  },
  jobbizGame: {
    key: 'jobbiz_game',
    label: 'Jobbiz Game',
    to: '/jobbiz-game',
    exact: true,
    icon: GamepadIcon,
  },
  intercom: {
    key: 'intercom',
    label: 'Intercom',
    href: 'https://app.intercom.com/',
    icon: IntercomIcon,
  },
};

// The menu: the dashboard, then groups that fold behind a chevron, the way
// Viviers used to. A recruiter (520) does no commercial work: the planning
// and the companies sit at the top level instead of the Commercial group.
export const SIDEBAR_SECTIONS = [
  LINKS.dashboard,
  LINKS.tasks,
  { ...LINKS.planning, show: isRecruiter },
  { ...LINKS.companies, show: isRecruiter },
  {
    key: 'commercial',
    label: 'Commercial',
    icon: HandshakeIcon,
    items: [LINKS.prospection, LINKS.stats, LINKS.companies, LINKS.planning],
    show: (user) => !isRecruiter(user),
  },
  {
    key: 'recruitment',
    label: 'Recrutement',
    icon: UserSearchIcon,
    items: [
      LINKS.missions,
      LINKS.candidates,
      LINKS.pools,
      LINKS.reviews,
      LINKS.identityValidation,
      LINKS.qualifications,
    ],
  },
  {
    key: 'payroll_billing',
    label: 'Paie et facturation',
    icon: ReceiptIcon,
    items: [LINKS.timesheets, LINKS.payroll, LINKS.billing],
  },
];

// The bottom bar on a phone; its « Plus » button opens the whole sidebar.
export const MOBILE_TABS = [
  LINKS.dashboard,
  LINKS.timesheets,
  LINKS.companies,
  LINKS.missions,
];

// The admin console has its own sidebar, as on the prototype: the members,
// then the settings, and the way back to the CRM at the bottom. The settings
// the admin does not have yet show greyed (`disabled`).
export const ADMIN_LINKS = {
  members: {
    key: 'admin_members',
    label: 'Membres',
    to: '/admin-console',
    exact: true,
    icon: GroupPeople,
  },
  sizes: {
    key: 'admin_sizes',
    label: 'Taille des clients',
    to: '/admin-console/taille-clients',
    icon: Building,
  },
  agencyGoals: {
    key: 'admin_agency_goals',
    label: 'Objectifs de l’agence',
    to: '/admin-console/objectifs-agence',
    icon: TrendingUpIcon,
  },
  goals: {
    key: 'admin_goals',
    label: 'Objectifs des commerciaux',
    to: '/admin-console/objectifs',
    icon: ChartColumnIcon,
  },
  back: {
    key: 'admin_back',
    label: 'Retour au CRM',
    to: '/dashboard',
    exact: true,
    icon: ChevronLeft,
  },
};

export const ADMIN_SECTIONS = [
  ADMIN_LINKS.members,
  {
    key: 'admin_settings',
    label: 'Paramètres',
    icon: CogIcon,
    items: [
      { key: 'admin_appointments', label: 'Rendez-vous clients', icon: CalendarDaysIcon, disabled: true },
      { key: 'admin_calls', label: 'Appels', icon: PhoneIcon, disabled: true },
      ADMIN_LINKS.agencyGoals,
      ADMIN_LINKS.goals,
      ADMIN_LINKS.sizes,
      { key: 'admin_etp', label: 'ETP', icon: GraphIcon, disabled: true },
    ],
  },
];

export const isAdminConsole = (pathname) =>
  pathname === '/admin-console' || pathname.startsWith('/admin-console/');

export const canCreateMission = isStaff;

// The « Créer » menu and the open tasks count beside « Mes tâches » go with
// the tasks, which the Commercial has too; only « Une mission » inside the
// menu stays with canCreateMission.
export const canUseCreateMenu = worksWithCompanies;

export const isLinkVisible = (link, user) => !link.show || link.show(user);

export const isLinkActive = (link, pathname) => {
  if (!link.to) return false;
  if (link.exact) return pathname === link.to;
  return [link.to, ...(link.match ?? [])].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
};

// The sections a user may see: a group keeps its visible links, and goes when
// none is left or when it is hidden from the user.
export const visibleSections = (user) =>
  SIDEBAR_SECTIONS.map((section) =>
    section.items
      ? {
          ...section,
          items: section.items.filter((link) => isLinkVisible(link, user)),
        }
      : section
  ).filter(
    (section) =>
      isLinkVisible(section, user) &&
      (!section.items || section.items.length > 0),
  );

// The menu page a path belongs to (the navbar takes its title from it).
export const activeLink = (pathname) =>
  Object.values(LINKS).find((link) => isLinkActive(link, pathname));

export const linkLabel = (language, item) =>
  language.getValue(`sidebar.${item.key}`, item.label);
